import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { toast } from "sonner";

type Fan = {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  athlete_favorites_count: number;
  team_favorites_count: number;
  product_favorites_count: number;
  cause_favorites_count: number;
};

const AdminFans = () => {
  const [fans, setFans] = useState<Fan[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc("admin_list_fans");
      if (error) toast.error(error.message);
      setFans(((data as any) ?? []).map((f: any) => ({
        ...f,
        athlete_favorites_count: Number(f.athlete_favorites_count) || 0,
        team_favorites_count: Number(f.team_favorites_count) || 0,
        product_favorites_count: Number(f.product_favorites_count) || 0,
        cause_favorites_count: Number(f.cause_favorites_count) || 0,
      })));
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return fans;
    return fans.filter(
      (f) => f.email.toLowerCase().includes(s) || (f.display_name ?? "").toLowerCase().includes(s),
    );
  }, [fans, q]);

  const totalFavs = (f: Fan) =>
    f.athlete_favorites_count + f.team_favorites_count + f.product_favorites_count + f.cause_favorites_count;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl">Fans</h1>
          <p className="text-sm text-muted-foreground mt-1">{fans.length} fan {fans.length === 1 ? "account" : "accounts"}</p>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email or name…"
          className="px-4 py-2 rounded-full bg-card border border-border text-sm w-72"
        />
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground">No fans yet.</p>
      ) : (
        <div className="bg-card rounded-2xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Signed up</th>
                  <th className="text-left px-4 py-3">Last sign-in</th>
                  <th className="text-right px-4 py-3">Athletes</th>
                  <th className="text-right px-4 py-3">Teams</th>
                  <th className="text-right px-4 py-3">Brands</th>
                  <th className="text-right px-4 py-3">Causes</th>
                  <th className="text-right px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr key={f.id} className="border-t border-border">
                    <td className="px-4 py-3 font-semibold">{f.display_name ?? "—"}</td>
                    <td className="px-4 py-3">{f.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {f.last_sign_in_at ? new Date(f.last_sign_in_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">{f.athlete_favorites_count}</td>
                    <td className="px-4 py-3 text-right">{f.team_favorites_count}</td>
                    <td className="px-4 py-3 text-right">{f.product_favorites_count}</td>
                    <td className="px-4 py-3 text-right">{f.cause_favorites_count}</td>
                    <td className="px-4 py-3 text-right font-semibold">{totalFavs(f)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFans;
